import type { DetailedJobRequisition } from '../../functions/api/_ukg'

import { h, render } from 'preact'
import { useEffect, useState } from 'preact/hooks'

type SortValue = {
	sortBy: 'job_title' | 'location' | 'company' | 'job_type'
	direction: 'asc' | 'desc'
}

const COMPANY_NAMES: Record<number, string> = {
	102042497: 'Apical',
	101977410: 'Ctrl Alt Destroy Inc',
	102458304: 'Embr - Union Leaf',
	101977412: "Farmer J's",
	101977409: 'Greenfield Organix 4th Street',
	101954880: 'Grow Op Farms',
	102223232: 'Kur Retailers',
	102042496: 'Modern Leaf',
	101977411: 'Panda Solutions',
}

function getCompanyName(einId: number) {
	return COMPANY_NAMES[einId] ?? 'Other'
}

function CareersDisplay() {
	const [data, setData] = useState<Awaited<ReturnType<typeof getJobs>> | null>(
		null,
	)

	useEffect(() => {
		getJobs().then(setData)
	}, [])

	const [selectedLocation, setSelectedLocation] = useState('all')
	// console.log('Selected Location:', selectedLocation)

	const [selectedCompany, setSelectedCompany] = useState('all')
	// console.log('Selected Company:', selectedCompany)

	const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([])
	// console.log('Selected Job Types:', selectedJobTypes)

	const [selectedSort, setSelectedSort] = useState<SortValue>({
		sortBy: 'job_title',
		direction: 'asc',
	})

	const filteredJobs = (data?.jobs ?? []).filter(job => {
		if (
			selectedLocation !== 'all' &&
			job.location_string !== selectedLocation
		) {
			return false
		}
		if (selectedCompany !== 'all' && job.company_name !== selectedCompany) {
			return false
		}
		if (
			selectedJobTypes.length > 0 &&
			!selectedJobTypes.includes(job.employee_type.name)
		) {
			return false
		}
		return true
	})

	const sortedJobs = filteredJobs.toSorted((a, b) => {
		switch (selectedSort.sortBy) {
			case 'job_title':
				return selectedSort.direction === 'asc'
					? a.job_title.localeCompare(b.job_title)
					: b.job_title.localeCompare(a.job_title)

			case 'location':
				return selectedSort.direction === 'asc'
					? a.location_string.localeCompare(b.location_string)
					: b.location_string.localeCompare(a.location_string)

			case 'company':
				return selectedSort.direction === 'asc'
					? a.company_name.localeCompare(b.company_name)
					: b.company_name.localeCompare(a.company_name)

			case 'job_type':
				return selectedSort.direction === 'asc'
					? a.employee_type.name.localeCompare(b.employee_type.name)
					: b.employee_type.name.localeCompare(a.employee_type.name)

			default:
				return 0
		}
	})

	const getSortSymbol = (sortBy: SortValue['sortBy']) => {
		if (selectedSort.sortBy === sortBy) {
			return selectedSort.direction === 'asc' ? '↑' : '↓'
		}
		return ''
	}

	const createSortHandler = (sortBy: SortValue['sortBy']) => {
		return () => {
			setSelectedSort(prev => {
				if (prev.sortBy === sortBy) {
					// If the same column is clicked, toggle direction
					return {
						sortBy,
						direction: prev.direction === 'asc' ? 'desc' : 'asc',
					}
				} else {
					// If a new column is clicked, default to ascending
					return { sortBy, direction: 'asc' }
				}
			})
		}
	}

	return (
		<div class="content-wrapper" data-animate="fadeInUp">
			<div class="title-wrapper">
				<h1>Careers</h1>
				<h2>Open Positions</h2>
			</div>
			<div class="form-wrapper">
				<form id="job_filters">
					<div class="filter-preamble">
						<h3>Filters:</h3>
						<button
							class="clear-filters"
							aria-label="Clear All Filters"
							type="reset"
							onClick={e => {
								e.preventDefault()
								setSelectedLocation('all')
								setSelectedCompany('all')
								setSelectedJobTypes([])
							}}
						>
							Clear All
						</button>
					</div>
					<div class="select-checkbox-wrapper">
						<div class="select-wrapper">
							<div class="select-field">
								<select
									name="location"
									id="location"
									aria-label="Filter by location"
									value={selectedLocation}
									onChange={e => setSelectedLocation(e.currentTarget.value)}
								>
									<option value="all">LOCATIONS</option>
									{data?.locations.map(location => (
										<option value={location}>{location}</option>
									))}
								</select>
							</div>
							<div class="select-field">
								<select
									name="company"
									id="company"
									aria-label="Filter by company"
									value={selectedCompany}
									onChange={e => setSelectedCompany(e.currentTarget.value)}
								>
									<option value="all">COMPANIES</option>
									{data?.companies.map(company => (
										<option value={company}>{company}</option>
									))}
								</select>
							</div>
						</div>

						<div class="checkbox-wrapper">
							<div class="checkbox-title">Job Type</div>
							<fieldset id="job_type" aria-label="Job Type">
								{data?.job_types.map(jobType => (
									<label>
										<input
											type="checkbox"
											name="job_type"
											value={jobType}
											checked={selectedJobTypes.includes(jobType)}
											onChange={e => {
												setSelectedJobTypes(prev => {
													if (e.currentTarget.checked) {
														return [...prev, jobType]
													} else {
														return prev.filter(type => type !== jobType)
													}
												})
											}}
										/>
										{jobType}
									</label>
								))}
							</fieldset>
						</div>
					</div>
				</form>
			</div>

			<div class="jobs-wrapper" id="jobs_list">
				<button
					class="title-sort"
					onClick={createSortHandler('job_title')}
					aria-label="Sort by Job Role"
				>
					Job Role <span>{getSortSymbol('job_title')}</span>
				</button>
				<button
					class="job-type-sort"
					onClick={createSortHandler('job_type')}
					aria-label="Sort by Job Type"
				>
					Job Type <span>{getSortSymbol('job_type')}</span>
				</button>
				<button
					class="company-sort"
					onClick={createSortHandler('company')}
					aria-label="Sort by Company"
				>
					Company <span>{getSortSymbol('company')}</span>
				</button>
				<button
					class="location-sort"
					onClick={createSortHandler('location')}
					aria-label="Sort by Location"
				>
					Location <span>{getSortSymbol('location')}</span>
				</button>
				<div class="jobs_list-body">
					{sortedJobs.map(job => (
						<a
							href={`https://secure6.saashr.com/ta/6155496.careers?TrackId=GrowOpFarmsWebsite&ApplyToJob=${job.id}&full_apply=&jobid=${job.id}`}
							target="_blank"
							rel="noopener noreferrer"
							class="job_list-row"
							aria-label={`${job.job_title}, ${job.employee_type.name}, ${job.location_string} (opens in new tab)`}
							data-location={`${job.location.city}, ${job.location.state}`}
							data-company={job.company_name}
							data-job_type={job.employee_type.name}
						>
							<span class="title">{job.job_title}</span>
							<span class="job_type">{job.employee_type.name}</span>
							<span class="company">{job.company_name}</span>
							<span class="location">{job.location_string}</span>
						</a>
					))}
				</div>
			</div>
		</div>
	)
}

const mountPoint = document.querySelector('.careers')
if (mountPoint) {
	render(<CareersDisplay />, mountPoint)
} else {
	console.error('Mount point for careers display not found')
}

// NEW CODE for UKG API version
async function getJobs() {
	const url = '/api/get-jobs'

	const response = await fetch(url)
	if (!response.ok) {
		throw new Error(`Response status: ${response.status}`)
	}
	const jobs = ((await response.json()) as DetailedJobRequisition[]).map(
		job => ({
			...job,
			company_name: getCompanyName(job.ein_id),
			location_string: `${job.location.city}, ${job.location.state}`,
		}),
	)

	const locations: string[] = []
	const companies: string[] = []
	const job_types: string[] = []
	for (const job of jobs) {
		locations.push(job.location_string)
		companies.push(job.company_name)
		job_types.push(job.employee_type.name)
	}
	const u_locations = [...new Set(locations)]
	const u_companies = [...new Set(companies)].toSorted((a, b) =>
		a.localeCompare(b),
	)
	const u_job_types = [...new Set(job_types)]

	return {
		locations: u_locations,
		companies: u_companies,
		job_types: u_job_types,
		jobs: jobs,
	}
}
